import { customElement, property } from "lit/decorators.js";
import type { PropertyValues } from "lit";

import { SugarElement } from "../../../core/viewport/SugarCanvas";
import { Engine, Entity } from "../../../engine/Engine";
import IRoomDesigner from "../types/IRoomDesigner";
import { DesignerTransformer } from "../../../core/drag/DesignerTransformer";
import type { PointerGestureInterpreterElement } from "../../../core/input/PointerGestureInterpreterElement";
import { DesignerInteractionRouter } from "../interaction/DesignerInteractionRouter";
import { SelectInteractionController } from "../interaction/SelectInteractionController";
import { MeasureInteractionController } from "../measure/MeasureInteractionController";
import { SugarModel } from "../../../loader/SugarModel";
import { DESIGNER_SCENE_READY_EVENT } from "../../../core/events/DesignerEventContracts";
import type { Room } from "../../../shape/Scene";
import {
  ProductGizmoCapture,
  ProductGizmoMode,
  ProductTranslateGizmo,
} from "../transform/ProductTranslateGizmo";

/**
 * RD: `pointer-gesture-interpreter` → this → `configurator-bridge` → `room-designer`.
 * CFG: same stack without `room-designer` — pass `.scene` (Room) and self-gestures boot.
 */
@customElement("designer-transformer")
export class DesignerTransformerElement extends SugarElement {
  @property({ attribute: false })
  designer?: IRoomDesigner;

  /** CFG / thin host: product Room without IRoomDesigner. */
  @property({ attribute: false })
  scene?: Room;

  private _engineSceneListenerAttached = false;
  private _router?: DesignerInteractionRouter;
  private _gizmo?: ProductTranslateGizmo;
  private readonly _gizmoCapture = new ProductGizmoCapture();
  private readonly _gizmoMode = new ProductGizmoMode();
  private _detachSceneReady?: () => void;

  private readonly _onDesignerSceneReady = (ev: Event): void => {
    const d = (ev as CustomEvent<{ designer: IRoomDesigner }>).detail?.designer;
    if (d !== this.designer) return;
    this._tryCreateEntity();
  };

  override createEntity(): Entity | undefined {
    return undefined;
  }

  override firstUpdated(_changed: PropertyValues): void {
    const canvas = this.closest("sugar-viewport, sugar-canvas");
    const boot = (): void => {
      this._ensureEngineSceneListener();
      this._tryCreateEntity();
    };
    if (this._getEngine()) {
      boot();
    } else {
      canvas?.addEventListener("engine-ready", boot, { once: true });
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has("designer") || changed.has("scene")) {
      this._ensureEngineSceneListener();
      this._tryCreateEntity();
    }
  }

  disconnectedCallback(): void {
    const eng = this._getEngine();
    if (eng && this._router) {
      this.designer?.toolManager?.detachInteractionHost(this._router);
      eng.removeEntity(this._router);
      this._router = undefined;
    }
    if (eng && this._gizmo) {
      eng.removeEntity(this._gizmo);
      this._gizmo = undefined;
    }
    this._detachSceneReady?.();
    this._detachSceneReady = undefined;
    if (eng && this._engineSceneListenerAttached) {
      eng.removeEventListener(
        DESIGNER_SCENE_READY_EVENT,
        this._onDesignerSceneReady as EventListener,
      );
      this._engineSceneListenerAttached = false;
    }
    super.disconnectedCallback();
  }

  private _getEngine(): Engine | undefined {
    return (
      this.closest("sugar-viewport, sugar-canvas") as {
        engine?: Engine;
      } | null
    )?.engine;
  }

  private _ensureEngineSceneListener(): void {
    if (this._engineSceneListenerAttached) return;
    if (!this.designer) return;
    const events = this.eventPlatform?.designer.configuration.events;
    if (events) {
      this._detachSceneReady = events.on(
        DESIGNER_SCENE_READY_EVENT,
        this._onDesignerSceneReady,
      );
      this._engineSceneListenerAttached = true;
      return;
    }
    const eng = this._getEngine();
    if (!eng) return;
    eng.addEventListener(
      DESIGNER_SCENE_READY_EVENT,
      this._onDesignerSceneReady as EventListener,
    );
    this._engineSceneListenerAttached = true;
  }

  private _resolvePointerEventTarget(): EventTarget | null {
    const viewportInput = this.eventPlatform?.designer.input.events;
    if (viewportInput) return viewportInput;
    const prev = this.previousElementSibling;
    if (prev?.tagName === "POINTER-GESTURE-INTERPRETER") {
      return (
        (prev as PointerGestureInterpreterElement).pointerGestures?.events ??
        null
      );
    }
    const host = this.closest("sugar-viewport, sugar-canvas");
    const el = host?.querySelector(
      "pointer-gesture-interpreter",
    ) as PointerGestureInterpreterElement | null;
    return el?.pointerGestures?.events ?? null;
  }

  private _resolveScene(): Room | undefined {
    return this.designer?.scene ?? this.scene;
  }

  private _tryCreateEntity(): void {
    const scene = this._resolveScene();
    if (this.entity || !scene) return;

    const t = new DesignerTransformer(
      scene,
      this._resolvePointerEventTarget(),
      undefined,
      this.designer?.drawer?.planarGraphController,
      this.designer?.anchorAdapter,
      this.eventPlatform?.designer,
    );
    this.entity = t;

    const pointerEvents = this._resolvePointerEventTarget();
    const engine = this._getEngine();
    const drawer = this.designer?.drawer;
    const tools = this.designer?.toolManager;

    if (pointerEvents && engine && drawer && tools) {
      const channels = this.eventPlatform?.designer;
      this._router = new DesignerInteractionRouter(
        pointerEvents,
        this._gizmoCapture,
      );
      tools.registerPointerInteraction(
        "select",
        new SelectInteractionController(
          engine,
          drawer,
          t,
          this.designer!.selection,
          (model) => {
            this.designer?.removeModel(model);
            return true;
          },
          (segmentId) => this.designer?.deleteSegment(segmentId) ?? false,
          channels,
          this._gizmoCapture,
          this._gizmoMode,
        ),
      );
      if (channels) {
        const designer = this.designer!;
        this._gizmo = new ProductTranslateGizmo(
          channels,
          this._gizmoCapture,
          this._gizmoMode,
          designer.anchorAdapter,
          () => ({
            tool: designer.drawerState,
            plan: designer.renderMode === "2d",
            product:
              designer.selectedProduct instanceof SugarModel
                ? designer.selectedProduct
                : undefined,
          }),
        );
        engine.addEntity(this._gizmo);
      }
      const measurementApi = this.designer?.measurementApi;
      if (measurementApi) {
        const measure = new MeasureInteractionController(
          engine,
          drawer,
          t,
          measurementApi,
          tools,
        );
        tools.registerPointerInteraction(
          "measure",
          measure,
          (payload) =>
            measure.setSource(
              (payload?.sourceModel as SugarModel | undefined) ?? null,
            ),
        );
      }
      engine.addEntity(this._router);
      tools.attachInteractionHost(this._router);
    } else {
      // CFG / no room-designer tools: transformer owns gestures.
      t.enableSelfGestures();
    }

    this.designer?.registerTransformer?.(t);
    this.dispatchEvent(
      new CustomEvent("entity-created", {
        detail: t,
        bubbles: true,
      }),
    );
  }
}
